import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Papa from 'papaparse';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import { lightColors, themeBackgrounds } from '../../theme/theme';
import { setIsFirstParticipantAdded } from '../../features/auth/authSlice';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';

interface Participant {
  _id?: string;
  id: number;
  name: string;
  applicationId: string;
  applicationName: string;
  avatarColor: string;
  user_id?: string;
  organization_id?: string;
  account_status?: string;
  created_at?: string;
}

interface ApiParticipant {
  _id: string;
  organization_id: string;
  participant_id: string;
  participant_name: string;
  app_id: string;
  app_name: string;
  created_at: string;
  participant_status?: string;
  __v: number;
}

interface InvalidParticipant {
  participant_id: string;
  participant_name: string;
  error: string;
  rowIndex: number;
  isFixed?: boolean;
}

export default function ApplicationParticipants(): React.ReactElement {
  const dispatch = useDispatch();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const appId = useSelector((state: any) => state.applicationDetails.app_id);
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [participantId, setParticipantId] = useState<string>('');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState<boolean>(true);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState<boolean>(false);
  const [bulkStatusUpdateOpen, setBulkStatusUpdateOpen] = useState<boolean>(false);
  const [statusToUpdate, setStatusToUpdate] = useState<'Active' | 'InActive'>('Active');
  const [isUploadingCSV, setIsUploadingCSV] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState<boolean>(false);
  const [invalidParticipants, setInvalidParticipants] = useState<InvalidParticipant[]>([]);
  const [validParticipants, setValidParticipants] = useState<Array<{ participant_id: string; participant_name: string }>>([]);

  useEffect(() => {
    if (!userDetails || !appId) return;
    fetchApplicationParticipants();
  }, [userDetails, appId, page, rowsPerPage]);

  const fetchApplicationParticipants = async (): Promise<void> => {
    if (!userDetails?.organization_id) return;

    setIsLoadingParticipants(true);
    try {
      const payload = {
        request_id: `get_participants_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "get_application_participants",
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
          app_id: appId || '',
          page: page + 1,
          limit: rowsPerPage
        }, REQ_ENC_KEY)
      };

      const response = await httpService.post<any>(
        `${API_BASE_URL}/user/getApplicationParticipantsForAdmin`,
        payload
      );

      if (response.data.status === 200 && response.data.data) {
        const decrypted = await decryptResponse<{
          participants: ApiParticipant[];
          pagination: {
            page: number;
            limit: number;
            totalRecords: number;
            totalPages: number;
            hasNextPage: boolean;
            hasPreviousPage: boolean;
          };
        }>(response.data.data, getSessionKey() as string);

        const { participants: pageParticipants, pagination } = decrypted;
        const transformedParticipants: Participant[] = pageParticipants.map((apiParticipant: ApiParticipant, index: number) => ({
          _id: apiParticipant._id,
          id: page * rowsPerPage + index + 1,
          name: apiParticipant.participant_name,
          applicationId: apiParticipant.app_id,
          applicationName: apiParticipant.app_name,
          avatarColor: lightColors[Math.floor(Math.random() * lightColors.length)],
          user_id: apiParticipant.participant_id,
          organization_id: apiParticipant.organization_id,
          account_status: apiParticipant.participant_status || 'Active',
          created_at: apiParticipant.created_at
        }));

        setParticipants(transformedParticipants);
        setTotalRecords(pagination?.totalRecords ?? transformedParticipants.length);
      } else {
        setParticipants([]);
        setTotalRecords(0);
      }
      setSelectedParticipants([]);
    } catch (error) {
      console.log('Error fetching application participants:', error);
      setParticipants([]);
      setTotalRecords(0);
    } finally {
      setIsLoadingParticipants(false);
    }
  };

  const handleAddParticipantClick = (): void => setOpenAddDialog(true);

  const handleDialogClose = (): void => {
    setName('');
    setParticipantId('');
    setOpenAddDialog(false);
  };

  const handleSelectParticipant = (participantId: string): void => {
    setSelectedParticipants(prev =>
      prev.includes(participantId)
        ? prev.filter(id => id !== participantId)
        : [...prev, participantId]
    );
  };

  const handleSelectAll = (): void => {
    if (selectedParticipants.length === filteredParticipants.length) {
      setSelectedParticipants([]);
    } else {
      const allIds = filteredParticipants.map(p => p.user_id || p._id || '').filter(Boolean);
      setSelectedParticipants(allIds);
    }
  };

  const handleBulkDelete = (): void => {
    setBulkDeleteOpen(true);
  };

  const confirmBulkDelete = async (): Promise<void> => {
    if (selectedParticipants.length === 0) return;

    setIsLoading(true);
    try {
      const bulkDeletePayload = {
        request_id: `bulk_delete_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "remove_multiple_application_participants",
        data: {
          organization_id: userDetails?.organization_id || '',
          app_id: appId || '',
          participant_ids: selectedParticipants
        }
      };

      type BulkDeleteResponse = {
        status: number;
        message?: string;
        data?: {
          removed_count: number;
          requested_count: number;
          not_found_count: number;
          participant_ids: string[];
        };
        error?: any;
        [key: string]: any;
      };

      const response = await httpService.post<BulkDeleteResponse>(
        `${API_BASE_URL}/user/removeApplicationParticipant`,
        bulkDeletePayload
      );

      console.log("Bulk Delete Response:", response);

      if (response.data.status === 200) {
        const { removed_count, not_found_count } = response.data.data || {};

        if (removed_count && removed_count > 0) {
          toast.success(`${removed_count} participant(s) removed successfully!`, {
            position: 'top-right',
            autoClose: 3000
          });
        }

        if (not_found_count && not_found_count > 0) {
          toast.warning(`${not_found_count} participant(s) were not found`, {
            position: 'top-right',
            autoClose: 3000
          });
        }

        // Refresh the participants list
        await fetchApplicationParticipants();
        setSelectedParticipants([]);
      } else {
        toast.error(response.data.message || 'Failed to remove participants. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('An error occurred while removing participants. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsLoading(false);
      setBulkDeleteOpen(false);
    }
  };

  const cancelBulkDelete = (): void => {
    setBulkDeleteOpen(false);
  };

  const handleBulkStatusUpdate = (status: 'Active' | 'InActive'): void => {
    setStatusToUpdate(status);
    setBulkStatusUpdateOpen(true);
  };

  const confirmBulkStatusUpdate = async (): Promise<void> => {
    if (selectedParticipants.length === 0) return;

    setIsLoading(true);
    try {
      const bulkStatusUpdatePayload = {
        request_id: `bulk_status_update_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "update_multiple_participant_status",
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          app_id: appId || '',
          participant_ids: selectedParticipants,
          participant_status: statusToUpdate
        }, REQ_ENC_KEY)
      };

      type BulkStatusUpdateResponse = {
        status: number;
        message?: string;
        data?: any;
        error?: any;
        [key: string]: any;
      };

      const response = await httpService.post<BulkStatusUpdateResponse>(
        `${API_BASE_URL}/user/updateParticipantStatus`,
        bulkStatusUpdatePayload
      );

      if (response.data.status === 200 && response.data.data) {
        const decryptedData = await decryptResponse<any>(response.data.data, getSessionKey() as string);
        const { updated_count, not_found_count } = decryptedData || {};

        if (updated_count && updated_count > 0) {
          toast.success(`${updated_count} participant(s) status updated to ${statusToUpdate} successfully!`, {
            position: 'top-right',
            autoClose: 3000
          });
        } else {
          toast.success("Request submitted successfully!", {
            position: 'top-right',
            autoClose: 3000
          });
        }

        if (not_found_count && not_found_count > 0) {
          toast.warning(`${not_found_count} participant(s) were not found`, {
            position: 'top-right',
            autoClose: 3000
          });
        }

        // Refresh the participants list
        await fetchApplicationParticipants();
        setSelectedParticipants([]);
      } else {
        toast.error(response.data.message || 'Failed to update participant status. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('An error occurred while updating participant status. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsLoading(false);
      setBulkStatusUpdateOpen(false);
    }
  };

  const cancelBulkStatusUpdate = (): void => {
    setBulkStatusUpdateOpen(false);
  };

  const handleCSVUploadClick = (): void => {
    fileInputRef.current?.click();
  };

  const validateParticipantName = (name: string): { isValid: boolean; error: string } => {
    const hasNumbers = /\d/.test(name);
    if (hasNumbers) {
      return { isValid: false, error: 'Name contains numbers' };
    }
    if (!name.trim() || !/[a-zA-Z]/.test(name)) {
      return { isValid: false, error: 'Name must contain letters' };
    }
    return { isValid: true, error: '' };
  };

  const handleCSVFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a valid CSV file', {
        position: 'top-right',
        autoClose: 3000
      });
      return;
    }

    setIsUploadingCSV(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          // Validate CSV structure
          if (!results.data || results.data.length === 0) {
            toast.error('CSV file is empty', {
              position: 'top-right',
              autoClose: 3000
            });
            setIsUploadingCSV(false);
            return;
          }

          // Transform CSV data to API format and validate
          const allParticipants = results.data.map((row: any, index: number) => ({
            participant_id: row.participant_id || row.Participant_ID || row['Participant ID'] || '',
            participant_name: row.participant_name || row.Participant_Name || row['Participant Name'] || row.name || row.Name || '',
            rowIndex: index
          })).filter(p => p.participant_id && p.participant_name);

          if (allParticipants.length === 0) {
            toast.error('No valid participants found in CSV. Please ensure columns are named "participant_id" and "participant_name"', {
              position: 'top-right',
              autoClose: 5000
            });
            setIsUploadingCSV(false);
            return;
          }

          // Validate participant names
          const validList: Array<{ participant_id: string; participant_name: string }> = [];
          const invalidList: InvalidParticipant[] = [];

          allParticipants.forEach((participant) => {
            const validation = validateParticipantName(participant.participant_name);
            if (validation.isValid) {
              validList.push({
                participant_id: participant.participant_id,
                participant_name: participant.participant_name
              });
            } else {
              invalidList.push({
                participant_id: participant.participant_id,
                participant_name: participant.participant_name,
                error: validation.error,
                rowIndex: participant.rowIndex
              });
            }
          });

          // If there are invalid participants, show validation dialog
          if (invalidList.length > 0) {
            setInvalidParticipants(invalidList);
            setValidParticipants(validList);
            setValidationDialogOpen(true);
            setIsUploadingCSV(false);
            return;
          }

          const participants = validList;

          // Prepare API payload
          const uploadPayload = {
            data: await encryptResponse({
              organization_id: userDetails?.organization_id || '',
              app_id: appId || '',
              participants: participants
            }, REQ_ENC_KEY)
          };

          type UploadParticipantsResponse = {
            status: number;
            message?: string;
            data?: any;
            error?: any;
          };

          // Upload to API
          const response = await httpService.post<UploadParticipantsResponse>(
            `${API_BASE_URL}/user/uploadApplicationParticipants`,
            uploadPayload
          );

          if (response.data.status === 200 && response.data.data) {
            const decryptedData = await decryptResponse<{
              addedParticipants: Array<{ participant_id: string; participant_name: string }>;
              alreadyExistingParticipants: Array<{ participant_id: string; participant_name: string }>;
            }>(response.data.data, getSessionKey() as string);
            const { addedParticipants, alreadyExistingParticipants } = decryptedData || {};
            const addedCount = addedParticipants?.length || 0;
            const existingCount = alreadyExistingParticipants?.length || 0;

            if (addedCount > 0) {
              toast.success(`${addedCount} participant(s) uploaded successfully!`, {
                position: 'top-right',
                autoClose: 3000
              });
              if (userDetails && !userDetails.is_first_participant_added) {
                const payload = {
                  data: await encryptResponse({
                    organization_id: userDetails?.organization_id || '',
                    process_data: {
                      is_first_participant_added: true
                    }
                  }, REQ_ENC_KEY)
                };
                const response = await httpService.post<any>(`${API_BASE_URL}/user/updateUserProcess`, payload);
                console.log("Update User Process Response:", response);
                if (response.data?.status === 200) {
                  dispatch(setIsFirstParticipantAdded(true));
                }
              }
            }

            if (existingCount > 0) {
              toast.warning(`${existingCount} participant(s) already exist`, {
                position: 'top-right',
                autoClose: 3000
              });

              // Download CSV with failed participants
              downloadFailedParticipantsCSV(alreadyExistingParticipants || []);
            }

            // Refresh participants list
            await fetchApplicationParticipants();
          } else {
            toast.error(response.data.message || 'Failed to upload participants. Please try again.', {
              position: 'top-right',
              autoClose: 3000
            });
          }
        } catch (error) {
          console.error('Error uploading participants:', error);
          toast.error('An error occurred while uploading participants. Please try again.', {
            position: 'top-right',
            autoClose: 3000
          });
        } finally {
          setIsUploadingCSV(false);
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        toast.error('Failed to parse CSV file. Please check the file format.', {
          position: 'top-right',
          autoClose: 3000
        });
        setIsUploadingCSV(false);
      }
    });
  };

  const handleInvalidParticipantChange = (index: number, field: 'participant_id' | 'participant_name', value: string): void => {
    const updated = [...invalidParticipants];
    updated[index][field] = value;
    
    if (field === 'participant_name') {
      const validation = validateParticipantName(value);
      if (validation.isValid && updated[index].participant_id.trim()) {
        updated[index].isFixed = true;
        updated[index].error = '';
        
        const validParticipant = {
          participant_id: updated[index].participant_id,
          participant_name: value
        };
        
        setValidParticipants(prev => {
          const exists = prev.some(p => p.participant_id === validParticipant.participant_id);
          if (!exists) {
            return [...prev, validParticipant];
          }
          return prev;
        });
      } else {
        updated[index].error = validation.error;
        updated[index].isFixed = false;
        setValidParticipants(prev => 
          prev.filter(p => p.participant_id !== updated[index].participant_id)
        );
      }
    }
    
    setInvalidParticipants(updated);
  };

  const handleRemoveInvalidParticipant = (index: number): void => {
    const updated = invalidParticipants.filter((_, i) => i !== index);
    setInvalidParticipants(updated);
  };

  const handleValidationDialogClose = (): void => {
    setValidationDialogOpen(false);
    setInvalidParticipants([]);
    setValidParticipants([]);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadAfterValidation = async (): Promise<void> => {
    // Re-validate all invalid participants
    const stillInvalid: InvalidParticipant[] = [];
    const nowValid: Array<{ participant_id: string; participant_name: string }> = [];

    invalidParticipants.forEach((participant) => {
      const validation = validateParticipantName(participant.participant_name);
      if (validation.isValid && participant.participant_id.trim()) {
        nowValid.push({
          participant_id: participant.participant_id,
          participant_name: participant.participant_name
        });
      } else {
        stillInvalid.push({
          ...participant,
          error: validation.error || 'Invalid participant ID'
        });
      }
    });

    // If there are still invalid participants, show error
    if (stillInvalid.length > 0) {
      setInvalidParticipants(stillInvalid);
      toast.error(`${stillInvalid.length} participant(s) still have validation errors. Please fix them before uploading.`, {
        position: 'top-right',
        autoClose: 3000
      });
      return;
    }

    // Combine all valid participants and remove duplicates based on participant_id
    const allValidParticipants = [...validParticipants, ...nowValid];
    const uniqueParticipants = allValidParticipants.reduce((acc, current) => {
      const exists = acc.find(item => item.participant_id === current.participant_id);
      if (!exists) {
        acc.push(current);
      }
      return acc;
    }, [] as Array<{ participant_id: string; participant_name: string }>);

    if (uniqueParticipants.length === 0) {
      toast.error('No valid participants to upload', {
        position: 'top-right',
        autoClose: 3000
      });
      return;
    }

    // Close dialog and upload
    setValidationDialogOpen(false);
    setIsUploadingCSV(true);

    try {
      // Prepare API payload
      const uploadPayload = {
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          app_id: appId || '',
          participants: uniqueParticipants
        }, REQ_ENC_KEY)
      };

      type UploadParticipantsResponse = {
        status: number;
        message?: string;
        data?: any;
        error?: any;
      };

      // Upload to API
      const response = await httpService.post<UploadParticipantsResponse>(
        `${API_BASE_URL}/user/uploadApplicationParticipants`,
        uploadPayload
      );

      if (response.data.status === 200 && response.data.data) {
        const decryptedData = await decryptResponse<{
          addedParticipants: Array<{ participant_id: string; participant_name: string }>;
          alreadyExistingParticipants: Array<{ participant_id: string; participant_name: string }>;
        }>(response.data.data, getSessionKey() as string);
        const { addedParticipants, alreadyExistingParticipants } = decryptedData || {};
        const addedCount = addedParticipants?.length || 0;
        const existingCount = alreadyExistingParticipants?.length || 0;

        if (addedCount > 0) {
          toast.success(`${addedCount} participant(s) uploaded successfully!`, {
            position: 'top-right',
            autoClose: 3000
          });
          if (userDetails && !userDetails.is_first_participant_added) {
            const payload = {
              data: await encryptResponse({
                organization_id: userDetails?.organization_id || '',
                process_data: {
                  is_first_participant_added: true
                }
              }, REQ_ENC_KEY)
            };
            const response = await httpService.post<any>(`${API_BASE_URL}/user/updateUserProcess`, payload);
            console.log("Update User Process Response:", response);
            if (response.data?.status === 200) {
              dispatch(setIsFirstParticipantAdded(true));
            }
          }
        }

        if (existingCount > 0) {
          toast.warning(`${existingCount} participant(s) already exist`, {
            position: 'top-right',
            autoClose: 3000
          });

          // Download CSV with failed participants
          downloadFailedParticipantsCSV(alreadyExistingParticipants || []);
        }

        // Refresh participants list
        await fetchApplicationParticipants();
      } else {
        toast.error(response.data.message || 'Failed to upload participants. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Error uploading participants:', error);
      toast.error('An error occurred while uploading participants. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsUploadingCSV(false);
      setInvalidParticipants([]);
      setValidParticipants([]);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadFailedParticipantsCSV = (failedParticipants: Array<{ participant_id: string; participant_name: string }>): void => {
    if (failedParticipants.length === 0) return;

    // Convert to CSV format
    const csvContent = Papa.unparse(failedParticipants, {
      header: true,
      columns: ['participant_id', 'participant_name', 'reason'],
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `failed_participants_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.info('Failed participants downloaded as CSV', {
      position: 'top-right',
      autoClose: 3000
    });
  };

  const handleAddParticipant = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const addParticipantPayload = {
        request_id: `add_participant_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "add_application_participant",
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          app_id: appId || '',
          participant_id: participantId,
          participant_name: name
        }, REQ_ENC_KEY)
      };

      type AddParticipantResponse = {
        status: number;
        data?: any;
        message?: string;
        error?: any;
        [key: string]: any
      };

      const response = await httpService.post<AddParticipantResponse>(
        `${API_BASE_URL}/user/addApplicationParticipantForAdmin`,
        addParticipantPayload
      );

      if (response.data.status === 200 && response.data.data) {
        // Refresh the participants list
        await fetchApplicationParticipants();
        handleDialogClose();
        if (userDetails && !userDetails.is_first_participant_added) {
          const payload = {
            data: await encryptResponse({
              organization_id: userDetails?.organization_id || '',
              process_data: {
                is_first_participant_added: true
              }
            }, REQ_ENC_KEY)
          };
          const response = await httpService.post<any>(`${API_BASE_URL}/user/updateUserProcess`, payload);
          console.log("Update User Process Response:", response);
          if (response.data?.status === 200) {
            dispatch(setIsFirstParticipantAdded(true));
          }
        }
        toast.success('Participant added successfully!', {
          position: 'top-right',
          autoClose: 3000
        });
      } else {
        toast.error(response.data.message || 'Failed to add participant. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('An error occurred while adding the participant. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number): void => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search filters the currently loaded page (the API has no search param yet).
  const filteredParticipants: Participant[] = participants.filter(participant =>
    participant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    participant.applicationName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <PageHeader
          heading="Application Participants"
          subheading="Manage participants associated with your applications"
          variant="gradient"
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <TextField
          placeholder="Search participants, applications..."
          size="small"
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{ endAdornment: <IconButton><SearchIcon /></IconButton> }}
        />
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={handleAddParticipantClick}
        >
          Add Participant
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={handleCSVUploadClick}
          disabled={isUploadingCSV}
          sx={{
            background: isUploadingCSV ? 'grey' : themeBackgrounds.default,
            color: '#ffffff',
            '&:hover': {
              background: themeBackgrounds.default,
              opacity: 0.9,
            },
          }}
        >
          {isUploadingCSV ? 'Uploading...' : 'Upload CSV'}
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleCSVFileChange}
        />
        {selectedParticipants.length > 0 && (
          <>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => handleBulkStatusUpdate('Active')}
              sx={{ mr: 1 }}
            >
              Activate Selected ({selectedParticipants.length})
            </Button>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => handleBulkStatusUpdate('InActive')}
              sx={{ mr: 1 }}
            >
              Deactivate Selected ({selectedParticipants.length})
            </Button>
            {/* <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
            >
              Delete Selected ({selectedParticipants.length})
            </Button> */}
          </>
        )}
      </Box>

      <Paper>
        {isLoadingParticipants ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading participants...</Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedParticipants.length > 0 && selectedParticipants.length < filteredParticipants.length}
                      checked={filteredParticipants.length > 0 && selectedParticipants.length === filteredParticipants.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Participant ID</TableCell>
                  <TableCell>Application</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredParticipants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No participants found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredParticipants.map((participant) => {
                    const participantId = participant.user_id || participant._id || '';
                    const isSelected = selectedParticipants.includes(participantId);

                    return (
                      <TableRow key={participant._id || participant.id} selected={isSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectParticipant(participantId)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ bgcolor: participant.avatarColor }}>
                              {participant.name.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" fontWeight="medium">
                              {participant.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {participant.user_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {participant.applicationName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              bgcolor:
                                participant.account_status === 'Active' ? '#e8f5e8' :
                                  participant.account_status === 'Inactive' ? '#fff3cd' :
                                    participant.account_status === 'Pending' ? '#e3f2fd' :
                                      participant.account_status === 'Blocked' ? '#ffebee' :
                                        '#f5f5f5',
                              color:
                                participant.account_status === 'Active' ? '#2e7d32' :
                                  participant.account_status === 'Inactive' ? '#856404' :
                                    participant.account_status === 'Pending' ? '#1565c0' :
                                      participant.account_status === 'Blocked' ? '#c62828' :
                                        '#666666'
                            }}
                          >
                            {participant.account_status || 'Active'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {participant.created_at ? new Date(participant.created_at).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
      <Dialog open={openAddDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add Application Participant
          <IconButton onClick={handleDialogClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add a new participant to an application.
          </Typography>

          <TextField
            fullWidth
            label="Participant Name"
            placeholder="Enter participant name"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Participant ID"
            placeholder="Enter participant ID"
            value={participantId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setParticipantId(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!name || !participantId || isLoading}
            onClick={handleAddParticipant}
            sx={{
              background: (!name || !participantId || isLoading) ? 'grey' : themeBackgrounds.default,
              color: '#ffffff',
              '&:hover': {
                background: themeBackgrounds.default,
                opacity: 0.9,
              },
            }}
          >
            {isLoading ? 'Adding...' : 'Add Participant'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={bulkStatusUpdateOpen} onClose={cancelBulkStatusUpdate} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Status Update</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to update the status of <strong>{selectedParticipants.length}</strong> selected participant(s) to <strong>{statusToUpdate}</strong>?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This will change the participant status in the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelBulkStatusUpdate} color="inherit" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={confirmBulkStatusUpdate}
            color={statusToUpdate === 'Active' ? 'success' : 'warning'}
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Updating...' : `Update to ${statusToUpdate}`}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={bulkDeleteOpen} onClose={cancelBulkDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{selectedParticipants.length}</strong> selected participant(s)?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelBulkDelete} color="inherit" disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={confirmBulkDelete} color="error" variant="contained" disabled={isLoading}>
            {isLoading ? 'Deleting...' : 'Delete Selected'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Dialog for Correcting Invalid Participants */}
      <Dialog 
        open={validationDialogOpen} 
        onClose={handleValidationDialogClose} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Validation Errors Found
          <IconButton 
            onClick={handleValidationDialogClose} 
            sx={{ ml: 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {invalidParticipants.filter(p => !p.isFixed).length} participant(s) have validation errors. Please correct the errors below before uploading.
            {validParticipants.length > 0 && (
              <Box component="span" sx={{ color: 'success.main', fontWeight: 'bold', ml: 1 }}>
                ({validParticipants.length} valid participant(s) will be uploaded)
              </Box>
            )}
          </Typography>

          <Paper variant="outlined" sx={{ maxHeight: '400px', overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Participant ID</TableCell>
                  <TableCell>Participant Name</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invalidParticipants.map((participant, index) => (
                  <TableRow 
                    key={index}
                    sx={{
                      bgcolor: participant.isFixed ? '#e8f5e9' : 'transparent',
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {participant.rowIndex + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={participant.participant_id}
                        onChange={(e) => handleInvalidParticipantChange(index, 'participant_id', e.target.value)}
                        placeholder="Enter ID"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={participant.participant_name}
                        onChange={(e) => handleInvalidParticipantChange(index, 'participant_name', e.target.value)}
                        error={!participant.isFixed && !!participant.error}
                        placeholder="Enter valid name"
                      />
                    </TableCell>
                    <TableCell>
                      {participant.isFixed ? (
                        <Typography 
                          variant="caption" 
                          color="success.main"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold' }}
                        >
                          <CheckCircleIcon sx={{ fontSize: 16 }} />
                          Valid
                        </Typography>
                      ) : (
                        <Typography 
                          variant="caption" 
                          color="error"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <WarningIcon sx={{ fontSize: 16 }} />
                          {participant.error}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveInvalidParticipant(index)}
                        title="Remove this participant"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
              Validation Rules:
            </Typography>
            <Typography variant="caption" display="block">
              • Participant names must not contain numbers
            </Typography>
            <Typography variant="caption" display="block">
              • Names must contain at least one letter
            </Typography>
            <Typography variant="caption" display="block">
              • Both participant ID and name are required
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleValidationDialogClose} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUploadAfterValidation}
            variant="contained"
            disabled={isUploadingCSV}
            sx={{
              background: isUploadingCSV ? 'grey' : themeBackgrounds.default,
              color: '#ffffff',
              '&:hover': {
                background: themeBackgrounds.default,
                opacity: 0.9,
              },
            }}
          >
            {isUploadingCSV ? 'Uploading...' : 'Upload Valid Participants'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
